import { SxProps, Theme } from "@mui/material"
import {
  outlinePalettes,
  TextFieldRequiredOpts,
  TextFieldRequiredPalette,
  TextFieldRequiredScheme,
} from "./TextFieldRequiredStyle"

type SelectRequiredOpts = TextFieldRequiredOpts & {
  size?: "small"
}

export const selectSx =
  ({
    errored,
    valid,
    filled,
    rounded,
    borderRadius,
    scheme = "default",
    overrides,
    size,
  }: SelectRequiredOpts): SxProps<Theme> =>
  (theme) => {
    const radiusCss =
      typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius

    // escolhe a paleta do modelo + aplica overrides
    const base = outlinePalettes(theme)[scheme]
    const palette: TextFieldRequiredPalette = { ...base, ...overrides }

    // 1) Determina o estado atual (prioridade: erro > válido > preenchido > default)
    const state = errored
      ? "error"
      : valid
      ? "valid"
      : filled
      ? "filled"
      : ("default" as "error" | "valid" | "filled" | "default")

    const baseColor =
      state === "error"
        ? palette.error
        : state === "valid"
        ? palette.valid
        : state === "filled"
        ? palette.filled
        : palette.default

    const hoverColor =
      state === "error" || state === "valid" ? baseColor : palette.hoverDefault

    const focusColor =
      state === "error"
        ? palette.error
        : state === "valid"
        ? palette.valid
        : palette.focusDefault

    const baseWidth = state === "default" ? 1 : 2

    return {
      backgroundColor: "#FFF",
      borderRadius: radiusCss ?? (rounded ? "50px" : undefined),
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: baseColor,
        borderWidth: baseWidth,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hoverColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: focusColor,
        borderWidth: 2,
      },
      ".MuiSvgIcon-root ": {
        fill: "#7E86A0 !important",
      },
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: "#BCC1FF",
        borderWidth: size === "small" ? "1px" : "2px",
      },
    }
  }

export type TextFieldRequiredLabelPalette = {
  textDefault: string
  textError: string
  textValid: string
  bgRest: string // fundo quando NÃO shrink (label dentro do campo)
  bgShrink: string // fundo quando shrink (use transparente)
}

const labelPaletteFromOutline = (
  theme: Theme,
  scheme: TextFieldRequiredScheme
): TextFieldRequiredLabelPalette => {
  const p = outlinePalettes(theme)[scheme]
  return {
    //textDefault: theme.palette.text.secondary,
    textDefault: p.focusDefault,
    textError: p.error,
    textValid: p.valid,
    bgRest: p.bg ?? "#FFF",
    bgShrink: "transparent",
  }
}

type LabelOpts = {
  labeled: boolean
  shouldShrink: boolean
  backgroundColorTitle?: string
  startAdornment?: React.ReactNode
  filled?: boolean
  focused?: boolean
  errored?: boolean
  valid?: boolean
  scheme?: TextFieldRequiredScheme
  overrides?: Partial<TextFieldRequiredLabelPalette>
}

export const labelSx =
  ({
    labeled,
    shouldShrink,
    backgroundColorTitle,
    startAdornment,
    filled,
    focused,
    errored = false,
    valid = false,
    scheme = "default",
    overrides,
  }: LabelOpts): SxProps<Theme> =>
  (theme) => {
    const base = labelPaletteFromOutline(theme, scheme)
    const pal: TextFieldRequiredLabelPalette = { ...base, ...overrides }

    if (labeled) {
      return {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.primary,
        mb: 0.5,
      }
    }

    const textColor = errored
      ? pal.textError
      : valid
      ? pal.textValid
      : pal.textDefault

    return {
      px: 1,
      fontSize: 14,
      lineHeight: "24px",
      borderRadius: "8px",
      background: !shouldShrink
        ? "transparent"
        : backgroundColorTitle
        ? `linear-gradient(${backgroundColorTitle}, #fff)`
        : "#fff",
      "&.MuiInputLabel-root.MuiInputLabel-shrink": {
        color: textColor,
        backgroundColor: !shouldShrink ? "transparent" : "#fff",
      },
      ...(startAdornment && { ml: focused || filled ? undefined : 14.5 }),
    }
  }
